import { useEffect, useState } from "react";
import { Alert, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { classNumberLabel } from "../util/classDisplay";
import { isPublishedFederationStatus } from "../util/placedByDivision";
import BannerList from "./BannerList";

// The federation statuses a class can be published under, as static keys.
const STATUS_KEYS = {
  UPLOADED: "ring.placedByDivisionStatusUploaded",
  SUBMITTED: "ring.placedByDivisionStatusSubmitted",
  CORRECTED: "ring.placedByDivisionStatusCorrected",
};

// #3405 -- the warning shown before Placed by division changes on classes that
// already have placings: what happens to them (re-placed or cleared, hand
// edits lost), which classes it touches, and which of those already have
// published results. Mounted only while a confirm dialog is open, so the
// published-results lookup (`loadPublished`, report-api's federation show
// state, the same status PublishResults reads) only runs when it is needed.
//
// The copy deliberately does not promise that auto-place will run later: a
// class whose rides are all final but cannot be auto-placed (an unbroken tie,
// a missing judge place) is cleared, and no ride will go final again to
// trigger it (#3404 judge note).
export default function PlacedByDivisionImpact({
  classes,
  loadPublished,
  testIdBase = "placed-by-division",
  // #3415: Places awarded re-places the same way and shares this warning, with
  // its own static copy for the first paragraph.
  warningKey = "ring.placedByDivisionWarning",
}) {
  const { t } = useTranslation("scoreadmin");
  const [published, setPublished] = useState({ state: "loading", byClassId: {} });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const byClassId = (await loadPublished?.()) || {};
        if (!cancelled) setPublished({ state: "ready", byClassId });
      } catch {
        if (!cancelled) setPublished({ state: "error", byClassId: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
    // loadPublished is a fresh closure per render in some callers; the lookup
    // is per mount (per confirm), not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const publishedClasses = classes.filter((c) => isPublishedFederationStatus(published.byClassId[c.id]));

  return (
    <Alert severity="warning" sx={{ mb: 2 }} data-testid={`${testIdBase}-impact`}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t(warningKey)}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t("ring.placedByDivisionWarningClasses", { count: classes.length })}
      </Typography>
      <BannerList
        bulleted
        testId={`${testIdBase}-impact-classes`}
        items={classes.map((c) => ({ key: c.id, label: classNumberLabel(c) }))}
        renderItem={(item) => item.label}
        sx={{ mt: 0.5, mb: 1 }}
      />
      {published.state === "error" && (
        <Typography variant="body2" data-testid={`${testIdBase}-published-error`}>
          {t("ring.placedByDivisionPublishedError")}
        </Typography>
      )}
      {publishedClasses.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("ring.placedByDivisionPublishedHeading", { count: publishedClasses.length })}
          </Typography>
          <BannerList
            bulleted
            testId={`${testIdBase}-published-classes`}
            items={publishedClasses.map((c) => ({
              key: c.id,
              text: t("ring.placedByDivisionPublishedRow", {
                label: classNumberLabel(c),
                status: t(STATUS_KEYS[published.byClassId[c.id]]),
              }),
            }))}
            renderItem={(item) => item.text}
            sx={{ mt: 0.5 }}
          />
        </>
      )}
    </Alert>
  );
}
